import { cn } from '@v2/utils/cn';
import { useI18n } from '@v2/providers/I18nProvider';
import { fmtBrlShort } from './homeFormat';

type Props = {
    selectedMonth: string;
    received: number;
    toReceive: number;
    toReceiveHint?: string;
    nextMonthAmount: number;
    nextMonthCount: number;
    nextMonthLabel: string;
    futureTotal: number;
};

export default function SellerHomeKpis({
    selectedMonth,
    received,
    toReceive,
    toReceiveHint,
    nextMonthAmount,
    nextMonthCount,
    nextMonthLabel,
    futureTotal,
}: Props) {
    const { t } = useI18n();
    const [y, m] = selectedMonth.split('-').map(Number);
    const monthShort = new Date(y, (m || 1) - 1, 1).toLocaleDateString('pt-BR', { month: 'short' });

    return (
        <section className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <Kpi
                tone="emerald"
                label={t('v2.my_commission.kpi_received', { month: monthShort.replace('.', '') })}
                value={fmtBrlShort(received)}
                hint={t('v2.my_commission.kpi_received_hint')}
            />
            <Kpi
                tone="amber"
                label={t('v2.my_commission.kpi_to_receive')}
                value={fmtBrlShort(toReceive)}
                hint={toReceiveHint || t('v2.my_commission.kpi_to_receive_empty')}
            />
            <Kpi
                tone="emerald"
                label={t('v2.my_commission.kpi_next_planned', { month: nextMonthLabel })}
                value={fmtBrlShort(nextMonthAmount)}
                hint={t('v2.my_commission.kpi_next_planned_hint', { count: String(nextMonthCount) })}
            />
            <Kpi
                tone="orange"
                label={t('v2.my_commission.kpi_future')}
                value={fmtBrlShort(futureTotal)}
                hint={t('v2.my_commission.kpi_future_hint')}
            />
        </section>
    );
}

function Kpi({
    label,
    value,
    hint,
    tone,
}: {
    label: string;
    value: string;
    hint: string;
    tone: 'emerald' | 'amber' | 'orange';
}) {
    const toneCls = {
        emerald: 'border-emerald-200 bg-emerald-50 text-emerald-950 dark:border-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-100',
        amber: 'border-amber-200 bg-amber-50 text-amber-950 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-100',
        orange: 'border-orange-200 bg-orange-50 text-orange-950 dark:border-orange-900 dark:bg-orange-950/40 dark:text-orange-100',
    }[tone];

    return (
        <div className={cn('rounded-2xl border p-4', toneCls)}>
            <p className="text-[10px] font-bold uppercase tracking-wide opacity-70">{label}</p>
            <p className="mt-1 text-2xl font-black tracking-tight">{value}</p>
            <p className="mt-0.5 text-xs opacity-70">{hint}</p>
        </div>
    );
}
